import {
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  IPolicyResponseItem,
  policyConfigV2,
  PolicyResourceType,
} from "shared";
import { When } from "react-if";
import { CheckCircleIcon } from "@heroicons/react/20/solid";
import { DateTime } from "luxon";

import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { useDebounce } from "../../../../hooks/useDebounce";
import { useContactParams } from "../../../../hooks/useContacts";
import { useFetchContactsQuery } from "../../../../services/api/contactsApi/contacts";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { LoadingOverlay } from "../../../Molecules/LoadingOverlay";
import { Divider } from "../../../../../common/Atoms/Divider";
import { Notification } from "../../../../../common/Atoms/Notification";
import { useGetInvoices, useInvoiceParams } from "../../../../hooks/useGetInvoices";
import { useInvoice } from "../../../../hooks/useInvoice";
import { DatePopover } from "../../../../../common/Components/DatePopover";
import { useGetSelectedOrganisation } from "../../../../hooks/useGetSelectedOrganisation";
import { usePreviewPolicy } from "../../../../hooks/usePreviewPolicy";
import { Button } from "../../../../../common/Atoms/Button";
import { useFetchContactApplicablePoliciesQuery } from "../../../../services/api/contactsApi/contact";
import { useAllQuotes } from "../../../../hooks/useQuotes";
import { useAccountingSystemTerm } from "../../../../hooks/useAccountingSystemTerm";
import { useCheckPolicyForQuoteQuery } from "../../../../services/api/policyApi/policy";

interface PolicyTestViewRootProps {
  onSave: (closeAfterSave?: boolean) => void;
  policyFormData: IPolicyResponseItem | null;
  formDataDirty: boolean;
}

export function PolicyTestViewRoot({
  onSave,
  policyFormData,
  formDataDirty,
}: PolicyTestViewRootProps) {
  const [searchStr, setSearchStr] = useState(``);
  const currentOrg = useGetSelectedOrganisation();
  const debouncedSearchStr = useDebounce(searchStr, 350);
  const {
    params: contactParams,
    updateParams: updateContactParams,
    resetParams: resetContactParams,
  } = useContactParams();

  const {
    params: invoiceParams,
    updateParams: updateInvoiceParams,
    resetParams: resetInvoiceParams,
  } = useInvoiceParams();
  const {
    data: invoices,
    isFetching: invoicesFetching,
    isLoading: invoicesLoading,
  } = useGetInvoices();
  // Search every group, not just the one in the URL, so out of group contacts show and explain why the policy will not apply
  const { data: contacts, isFetching: contactsFetching, isLoading: contactsLoading } = useFetchContactsQuery({
    ...contactParams,
    groupId: null,
    organisationId: currentOrg?.id,
  }, {
    skip: !currentOrg?.id,
    refetchOnMountOrArgChange: true,
  });
  const {
    quotes,
    isFetching: quotesFetching,
    isLoading: quotesLoading,
  } = useAllQuotes();
  const quoteTermSingular = useAccountingSystemTerm(`Quote`);
  const [selectedResource, setSelectedResource] = useState<{ id: string | number; type: PolicyResourceType} | null>(null);

  const allQuotes = useMemo(() => Object.values(quotes || {}).filter(Boolean).flat(), [quotes]);

  const {
    data: contactApplicablePolicies,
    isLoading: contactApplicablePoliciesLoading,
    isFetching: contactApplicablePoliciesFetching,
  } = useFetchContactApplicablePoliciesQuery(selectedResource?.type === PolicyResourceType.CONTACT ? {
    contactId: Number(selectedResource.id),
    organisationId: currentOrg?.id || ``,
  }: {
    contactId: 0,
    organisationId: ``,
  }, {
    skip: selectedResource?.type !== PolicyResourceType.CONTACT || !selectedResource || !currentOrg?.id || !selectedResource?.id,
  });
  const {
    data: quotePolicyCheck,
    isFetching: quotePolicyCheckFetching,
  } = useCheckPolicyForQuoteQuery({
    organisationId: currentOrg?.id || ``,
    policyId: Number(policyFormData?.policy?.id),
    quoteId: selectedResource?.id,
  }, {
    skip: selectedResource?.type !== PolicyResourceType.QUOTE || !currentOrg?.id || !policyFormData?.policy?.id,
    refetchOnMountOrArgChange: true,
  });
  const {
    data: invoice,
    isLoading: invoiceLoading,
    isFetching: invoiceFetching,
    refetch: refetchInvoice,
  } = useInvoice(selectedResource?.type === PolicyResourceType.INVOICE ? selectedResource.id : undefined);

  const { openPreviewPolicy } = usePreviewPolicy();

  const resourceType = useMemo(() => {
    if (!policyFormData?.policy) {
      return null;
    }

    return policyConfigV2[policyFormData.policy.policy_type]?.resourceType;
  }, [policyFormData?.policy]);

  useEffect(() => {
    if (debouncedSearchStr?.length >= 1) {
      if (resourceType === PolicyResourceType.CONTACT) {
        resetContactParams();

        updateContactParams({
          ...contactParams,
          searchStr: debouncedSearchStr,
          page: 1,
          limit: 6,
        });
      }

      if (resourceType === PolicyResourceType.INVOICE) {
        resetInvoiceParams();

        updateInvoiceParams({
          ...invoiceParams,
          invoiceTextSearch: debouncedSearchStr,
          page: 1,
          limit: 6,
        });
      }
    }
  }, [debouncedSearchStr]);

  const results: {
    label: string;
    value: string | number;
    meta?: string;
  }[] = useMemo(() => {
    if (!resourceType || !debouncedSearchStr) {
      return [];
    }

    if (resourceType === PolicyResourceType.CONTACT) {
      return contacts?.contacts.map(c => ({
        label: `${c.name}`,
        value: c.id,
      })) || [];
    }

    if (resourceType === PolicyResourceType.INVOICE) {
      return invoices?.invoices.map(i => ({
        label: `${i.invoice.number} - ${i.contact.name}`,
        value: i.invoice.id,
      })).slice(0, 6) || [];
    }

    if (resourceType === PolicyResourceType.QUOTE) {
      const search = debouncedSearchStr.toLowerCase();

      return allQuotes
        .filter(q =>
          q.number?.toLowerCase().includes(search) ||
          q.contactName?.toLowerCase().includes(search))
        .map(q => ({
          label: `${q.number} - ${q.contactName}`,
          value: q.id,
          meta: [
            q.totalAmountFormatted,
            q.expiryDateString ? `Expires ${DateTime.fromISO(q.expiryDateString).toFormat(`dd LLL yyyy`)}` : null,
          ].filter(Boolean).join(` · `),
        }))
        .slice(0, 6);
    }

    return [];
  }, [contacts, resourceType, debouncedSearchStr, invoices, allQuotes]);

  const checkResultData: Array<{ applies: boolean; message: string | React.ReactNode }> | null = useMemo(() => {
    if (!selectedResource) {
      return null;
    }

    if (!policyFormData?.policy?.id) {
      return null;
    }

    if (selectedResource.type === PolicyResourceType.CONTACT) {
      if (contactApplicablePolicies?.[policyFormData.policy.id]) {
        return null;
      }

      if (!contactApplicablePolicies?.applicablePolicies) {
        return null;
      }

      return [{
        applies: contactApplicablePolicies.applicablePolicies[policyFormData.policy.id]?.appliesToContact,
        message: contactApplicablePolicies.applicablePolicies[policyFormData.policy.id]?.message,
      }];
    }

    if (selectedResource.type === PolicyResourceType.INVOICE) {
      if (!invoice) {
        return null;
      }

      if (!invoice.policyCheck.policyResults?.[policyFormData.policy.id]) {
        return [{
          applies: false,
          message: `This invoice contact is not in the same group as this policy`,
        }];
      }

      const results = invoice.policyCheck.policyResults[policyFormData.policy.id];

      return results.map(r => {
        return {
          applies: r.applies,
          message: r.reason || (
            <span className={ `flex` }>
              <span className={ `mr-1` }>
                { `This policy will be applied on` }
              </span>
              <DatePopover
                date={ r.applies ? r.appliesOn : null }
                labelFormat={ `dd LLL yyyy` }
                organisationTimezone={ currentOrg?.validatedTimezone }
              />

              <When condition={ r.instalmentNumber !== undefined }>
                <span className={ `ml-1` }>
                  { `(Installment #${r.instalmentNumber})` }
                </span>
              </When>
            </span>
          ),
        };
      });
    }

    if (selectedResource.type === PolicyResourceType.QUOTE) {
      if (!quotePolicyCheck || quotePolicyCheckFetching) {
        return null;
      }

      if (!quotePolicyCheck.applies) {
        return [{
          applies: false,
          message: quotePolicyCheck.reason,
        }];
      }

      return [{
        applies: true,
        message: (
          <span className={ `flex` }>
            <span className={ `mr-1` }>
              { `This ${quoteTermSingular.toLowerCase()} reminder will be sent on` }
            </span>
            <DatePopover
              date={ quotePolicyCheck.appliesOn }
              labelFormat={ `dd LLL yyyy` }
              organisationTimezone={ currentOrg?.validatedTimezone }
            />
          </span>
        ),
      }];
    }

    return null;
  }, [selectedResource, invoice, contactApplicablePolicies, quotePolicyCheck, quotePolicyCheckFetching, quoteTermSingular]);

  function onResultClick(e, value: string | number) {
    e.preventDefault();
    e.stopPropagation();

    setSelectedResource({
      id: value,
      type: resourceType,
    });
  }

  useEffect(() => {
    if (resourceType ===PolicyResourceType.INVOICE && selectedResource) {
      refetchInvoice();
    }
  }, [selectedResource]);

  function getValueName(id: string | number): string | undefined | null {
    if (resourceType === PolicyResourceType.CONTACT) {
      return contacts?.contacts.find(c => c.id === id)?.name;
    }

    if (resourceType === PolicyResourceType.INVOICE) {
      return invoices?.invoices.find(i => i.invoice.id === id)?.invoice?.number;
    }

    if (resourceType === PolicyResourceType.QUOTE) {
      return allQuotes.find(q => q.id === id)?.number;
    }

    return null;
  }

  function onPreview() {
    if (selectedResource?.type === PolicyResourceType.INVOICE) {
      openPreviewPolicy({
        invoiceId: selectedResource.id.toString(),
        policyId: Number(policyFormData?.policy?.id),
      });
    }

    if (selectedResource?.type === PolicyResourceType.CONTACT) {
      openPreviewPolicy({
        contactId: selectedResource.id as number,
        policyId: Number(policyFormData?.policy?.id),
      });
    }

    if (selectedResource?.type === PolicyResourceType.QUOTE) {
      openPreviewPolicy({
        quoteId: selectedResource.id.toString(),
        policyId: Number(policyFormData?.policy?.id),
      });
    }
  }

  if (!resourceType) {
    return null;
  }

  return (
    <div className={ `` }>
      <Paragraph
        variant={ `help` }
      >
        { `Use this tool to test if this policy will apply to a particular ${resourceType}` }
      </Paragraph>

      <When condition={ formDataDirty }>
        <Notification
          type={ `warning` }
          title={ `Unsaved changes` }
          className={ `mt-4 mb-2` }
          buttons={ (
            <Button
              onClick={ () => onSave(false) }
            >
              { `Save Changes` }
            </Button>
          ) }
        >
          { `You have unsaved changes. Save these changes before you can preview.` }
        </Notification>
      </When>
      { /* Resource Search and Result */ }

      <InputTextAddon
        placeholder={ `Search for ${resourceType === PolicyResourceType.INVOICE ? `an` : `a`} ${resourceType} to test this policy on...` }
        value={ searchStr }
        onChange={ e => setSearchStr(e.value?.toString()) }
        className={ `mt-2` }
        disabled={ formDataDirty }
      />

      <LoadingOverlay
        loading={ contactsFetching || contactsLoading || invoicesFetching || invoicesLoading || contactApplicablePoliciesFetching || contactApplicablePoliciesLoading || quotesFetching || quotesLoading }
      >
        <div className={ `grid grid-cols-3 gap-4 mt-4 min-h-[50px]` }>
          { /* Results */ }
          { results.map(r => (
            <button
              key={ r.value }
              onClick={ e => onResultClick(e, r.value) }
              disabled={ invoiceLoading || invoiceFetching }
              className={ `hover:bg-gray-50 rounded-md p-4 flex items-center justify-center` }
            >
              <div className={ `min-w-0` }>
                <Paragraph
                  className={ `truncate` }
                  as={ `span` }
                >
                  { r.label }
                </Paragraph>
                <When condition={ !!r.meta }>
                  <Paragraph
                    className={ `truncate` }
                    variant={ `help` }
                    size={ `xs` }
                  >
                    { r.meta }
                  </Paragraph>
                </When>
              </div>
              <When condition={ selectedResource?.id === r.value }>
                <CheckCircleIcon className={ `w-4 h-4 text-green-500` } />
              </When>
            </button>
          )) }

          <When condition={ !results?.length && debouncedSearchStr?.length >= 1 }>
            <Paragraph
              className={ `col-span-3 text-center text-gray-500 pt-6` }
            >
              { `No results found` }
            </Paragraph>
          </When>
        </div>
        <When condition={ results?.length >= 6 && debouncedSearchStr?.length >= 1 }>
          <Paragraph
            className={ `text-center text-xs` }
            variant={ `help` }
          >
            { `upto 6 results shown` }
          </Paragraph>
        </When>
      </LoadingOverlay>

      { /* Results */ }
      <When condition={ !selectedResource && results.length > 1 }>
        <Paragraph
          variant={ `help` }
        >
          { `Select ${resourceType === PolicyResourceType.INVOICE ? `an` : `a`} ${resourceType} to view results` }
        </Paragraph>
      </When>
      <LoadingOverlay
        loading={ invoiceLoading || invoiceFetching || quotePolicyCheckFetching }
      >
        <div>
          <When condition={ !!selectedResource }>
            <Divider
              weight={ `light` }
              spacing={ `small` }
            />
            <When condition={ !!checkResultData?.length }>
              {
                () => (
                  <div>
                    {
                      checkResultData?.map((checkResult, index) => {
                        const canPreview = checkCanPreview(checkResult, policyFormData);

                        return (
                          <Notification
                            key={ index }
                            type={ checkResult.applies ? `success` : `warning` }
                            title={ (checkResult.applies ? `Policy applies` : `Policy does not apply`) + ` to ${getValueName(selectedResource?.id)}` }
                            className={ index > 0 ? `mt-4` : `` }
                          >
                            <span className={ `flex justify-between items-center` }>
                              { checkResult.message }

                              <When condition={ canPreview && index === 0 }>
                                { () => (
                                  <Button
                                    onClick={ onPreview }
                                  >
                                    { `Preview` }
                                  </Button>
                                ) }
                              </When>
                            </span>
                          </Notification>
                        );
                      })
                    }
                  </div>
                )
              }
            </When>
          </When>
        </div>
      </LoadingOverlay>
    </div>
  );
}

function checkCanPreview(checkResultData: { applies: boolean; message: string | React.ReactNode }, policyFormData?: IPolicyResponseItem): boolean {
  if (!policyFormData?.policy) {
    return false;
  }

  let canPreview = policyConfigV2[policyFormData.policy.policy_type]?.previewConfig?.canPreview;

  if (policyConfigV2[policyFormData.policy.policy_type]?.previewConfig?.mustApplyToPreview && canPreview) {
    canPreview = !!checkResultData?.applies;
  }

  return canPreview;
}
